import type {
  CampaignGroupCountrySummary,
  CampaignGroupSearchRequest,
} from 'omega-rtc/types/ts/journey-campaign-group-search';

export const CAMPAIGN_GROUP_TAB = 'campaign-groups';
export const CAMPAIGN_GROUP_QUERY_KEYS = {
  search: 'campaign_group_search',
  type: 'campaign_group_type',
  journeyType: 'campaign_group_journey_type',
  ownership: 'campaign_group_ownership',
  audience: 'campaign_group_audience',
  page: 'campaign_group_page',
  pageSize: 'campaign_group_page_size',
};
export const CAMPAIGN_GROUP_DEFAULT_PAGE_SIZE = 20;
export type CampaignGroupSearchParams = Record<string, string>;

const positiveInteger = (value: string, fallback: number, max = Number.MAX_SAFE_INTEGER) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 && number <= max ? number : fallback;
};

export const generateSearchCampaignGroupRequestBody = (
  params: CampaignGroupSearchParams,
): CampaignGroupSearchRequest => {
  const keys = CAMPAIGN_GROUP_QUERY_KEYS;
  const request: CampaignGroupSearchRequest = {
    // Journey is the only supported group type, and is required by UCD.
    filters: [{ lhs: 'groupType', operator: 'eq', rhs: 'JOURNEY' }],
    sort: { field: 'createdAt', asc: false },
    page: positiveInteger(params[keys.page], 1),
    pageSize: positiveInteger(params[keys.pageSize], CAMPAIGN_GROUP_DEFAULT_PAGE_SIZE, 500),
    ownedByMe: params[keys.ownership] === 'true',
  };
  const name = (params[keys.search] || '').trim();
  if (name) request.filters.push({ lhs: 'name', operator: 'match', rhs: name });
  const audience = (params[keys.audience] || '').toLowerCase();
  if (['pax', 'dax', 'mex'].includes(audience)) {
    request.filters.push({ lhs: 'audienceType', operator: 'eq', rhs: audience });
  }
  const journeyType = (params[keys.journeyType] || '').toUpperCase();
  if (['SINGLE_TOUCH', 'MULTI_TOUCH'].includes(journeyType)) {
    request.filters.push({ lhs: 'journeyType', operator: 'eq', rhs: journeyType });
  }
  return request;
};

export const updateCampaignGroupSearchParams = (
  params: CampaignGroupSearchParams,
  key: string,
  value?: string | number | boolean,
): CampaignGroupSearchParams => {
  const query = { ...params, tab: CAMPAIGN_GROUP_TAB };
  if (value === undefined || value === '' || value === false) delete query[key];
  else query[key] = String(value);
  if (key !== CAMPAIGN_GROUP_QUERY_KEYS.page) query[CAMPAIGN_GROUP_QUERY_KEYS.page] = '1';
  return query;
};

export const getDistinctCampaignGroupCountries = (
  countries: CampaignGroupCountrySummary[] = [],
): CampaignGroupCountrySummary[] => {
  const seenIDs: Set<string> = new Set();
  const seenCodes: Set<string> = new Set();
  return countries.filter(({ countryID, countryCode }) => {
    const id = String(countryID);
    const code = (countryCode || '').trim().toUpperCase();
    if (seenIDs.has(id) || (code && seenCodes.has(code))) return false;
    seenIDs.add(id);
    if (code) seenCodes.add(code);
    return true;
  });
};
